import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { SelectModule } from 'primeng/select';
import { MessageService, ConfirmationService } from 'primeng/api';
import { UbigeoService, UbigeoAdminDto } from '../../../core/services/ubigeo.service';

@Component({
  selector: 'app-ubigeo-admin',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    ToastModule,
    ConfirmDialogModule,
    TooltipModule,
    SelectModule
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './ubigeo-admin.html',
  styleUrls: ['./ubigeo-admin.css']
})
export class UbigeoAdminComponent implements OnInit {
  paises: UbigeoAdminDto[] = [];
  departamentos: UbigeoAdminDto[] = [];
  provincias: UbigeoAdminDto[] = [];
  distritos: UbigeoAdminDto[] = [];

  itemPais: Partial<UbigeoAdminDto> = {};
  itemDepto: Partial<UbigeoAdminDto> & { paisId?: number } = {};
  itemProv: Partial<UbigeoAdminDto> & { departamentoId?: number } = {};
  itemDistrito: Partial<UbigeoAdminDto> & { provinciaId?: number; codigoUbigeo?: string } = {};

  dialogPais = false;
  dialogDepto = false;
  dialogProv = false;
  dialogDistrito = false;
  submitted = false;

  paisesOptions: { id: number; nombre: string }[] = [];
  departamentosOptions: { id: number; nombre: string }[] = [];
  provinciasOptions: { id: number; nombre: string }[] = [];

  activeTab: 'paises' | 'departamentos' | 'provincias' | 'distritos' = 'paises';

  constructor(
    private ubigeo: UbigeoService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.loadAll();
    this.ubigeo.getAllPaises().subscribe({ next: (p) => { this.paisesOptions = p.filter(x => x.isActive !== false).map(x => ({ id: x.id, nombre: x.nombre })); this.cdr.detectChanges(); } });
  }

  loadAll() {
    this.ubigeo.getAllPaises().subscribe({ next: (p) => { this.paises = p; this.cdr.detectChanges(); } });
    this.ubigeo.getAllDepartamentos().subscribe({ next: (d) => { this.departamentos = d; this.cdr.detectChanges(); } });
    this.ubigeo.getAllProvincias().subscribe({ next: (p) => { this.provincias = p; this.cdr.detectChanges(); } });
    this.ubigeo.getAllDistritos().subscribe({ next: (d) => { this.distritos = d; this.cdr.detectChanges(); } });
  }

  // --- PAÍS ---
  openNewPais() {
    this.itemPais = {};
    this.submitted = false;
    this.dialogPais = true;
  }
  editPais(row: UbigeoAdminDto) {
    this.itemPais = { ...row };
    this.dialogPais = true;
  }
  savePais() {
    this.submitted = true;
    if (!this.itemPais.nombre?.trim()) return;
    const body = { nombre: this.itemPais.nombre };
    const req = this.itemPais.id ? this.ubigeo.updatePais(this.itemPais.id, body) : this.ubigeo.createPais(body);
    req.subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'País guardado' });
        this.loadAll();
        this.dialogPais = false;
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar' })
    });
  }
  togglePais(row: UbigeoAdminDto) {
    this.confirmationService.confirm({
      message: `¿${row.isActive ? 'Desactivar' : 'Activar'} "${row.nombre}"?`,
      header: 'Confirmar',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.ubigeo.togglePais(row.id).subscribe({ next: () => this.loadAll() })
    });
  }

  // --- DEPARTAMENTO ---
  openNewDepto() {
    this.itemDepto = {};
    this.submitted = false;
    this.dialogDepto = true;
  }
  editDepto(row: UbigeoAdminDto) {
    this.itemDepto = { ...row, paisId: row.paisId };
    this.dialogDepto = true;
  }
  saveDepto() {
    this.submitted = true;
    if (!this.itemDepto.nombre?.trim() || !this.itemDepto.paisId) return;
    const body = { nombre: this.itemDepto.nombre, paisId: this.itemDepto.paisId };
    const req = this.itemDepto.id ? this.ubigeo.updateDepartamento(this.itemDepto.id, body) : this.ubigeo.createDepartamento(body);
    req.subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Departamento guardado' });
        this.loadAll();
        this.dialogDepto = false;
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar' })
    });
  }
  toggleDepto(row: UbigeoAdminDto) {
    this.confirmationService.confirm({
      message: `¿${row.isActive ? 'Desactivar' : 'Activar'} "${row.nombre}"?`,
      header: 'Confirmar',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.ubigeo.toggleDepartamento(row.id).subscribe({ next: () => this.loadAll() })
    });
  }

  // --- PROVINCIA ---
  openNewProv() {
    this.itemProv = {};
    this.submitted = false;
    this.ubigeo.getAllDepartamentos().subscribe({ next: (d) => { this.departamentosOptions = d.filter(x => x.isActive !== false).map(x => ({ id: x.id, nombre: x.nombre })); this.cdr.detectChanges(); } });
    this.dialogProv = true;
  }
  editProv(row: UbigeoAdminDto) {
    this.itemProv = { ...row, departamentoId: row.departamentoId };
    this.ubigeo.getAllDepartamentos().subscribe({ next: (d) => { this.departamentosOptions = d.filter(x => x.isActive !== false).map(x => ({ id: x.id, nombre: x.nombre })); this.cdr.detectChanges(); } });
    this.dialogProv = true;
  }
  saveProv() {
    this.submitted = true;
    if (!this.itemProv.nombre?.trim() || !this.itemProv.departamentoId) return;
    const body = { nombre: this.itemProv.nombre, departamentoId: this.itemProv.departamentoId };
    const req = this.itemProv.id ? this.ubigeo.updateProvincia(this.itemProv.id, body) : this.ubigeo.createProvincia(body);
    req.subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Provincia guardada' });
        this.loadAll();
        this.dialogProv = false;
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar' })
    });
  }
  toggleProv(row: UbigeoAdminDto) {
    this.confirmationService.confirm({
      message: `¿${row.isActive ? 'Desactivar' : 'Activar'} "${row.nombre}"?`,
      header: 'Confirmar',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.ubigeo.toggleProvincia(row.id).subscribe({ next: () => this.loadAll() })
    });
  }

  // --- DISTRITO ---
  openNewDistrito() {
    this.itemDistrito = {};
    this.ubigeo.getAllProvincias().subscribe({ next: (p) => { this.provinciasOptions = p.filter(x => x.isActive !== false).map(x => ({ id: x.id, nombre: x.nombre })); this.cdr.detectChanges(); } });
    this.submitted = false;
    this.dialogDistrito = true;
  }
  editDistrito(row: UbigeoAdminDto) {
    this.itemDistrito = { ...row, provinciaId: row.provinciaId, codigoUbigeo: row.codigoUbigeo };
    this.ubigeo.getAllProvincias().subscribe({ next: (p) => { this.provinciasOptions = p.filter(x => x.isActive !== false).map(x => ({ id: x.id, nombre: x.nombre })); this.cdr.detectChanges(); } });
    this.dialogDistrito = true;
  }
  saveDistrito() {
    this.submitted = true;
    if (!this.itemDistrito.nombre?.trim() || !this.itemDistrito.provinciaId) return;
    const body = { nombre: this.itemDistrito.nombre, codigoUbigeo: this.itemDistrito.codigoUbigeo || undefined, provinciaId: this.itemDistrito.provinciaId };
    const req = this.itemDistrito.id ? this.ubigeo.updateDistrito(this.itemDistrito.id, body) : this.ubigeo.createDistrito(body);
    req.subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Distrito guardado' });
        this.loadAll();
        this.dialogDistrito = false;
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar' })
    });
  }
  toggleDistrito(row: UbigeoAdminDto) {
    this.confirmationService.confirm({
      message: `¿${row.isActive ? 'Desactivar' : 'Activar'} "${row.nombre}"?`,
      header: 'Confirmar',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.ubigeo.toggleDistrito(row.id).subscribe({ next: () => this.loadAll() })
    });
  }
}
